import { typographyTokens } from '../../theme/typographyTokens';
import React, { useMemo } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { Divider, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, NavigateFunction, Link } from 'react-router-dom';
import { useGetUserQuery } from '../../services/usersSlice';
import { useAccount } from '@azure/msal-react';
import { env } from '../../../shared/env';
import HelpIcon from '@mui/icons-material/Help';
import { useGetSettingsQuery } from '../../services/appSlice/appSlice';
import { useGetSepQuery } from '../../services/sepSlice';
import { useGetDataFieldsBySEPIDQuery } from '../../services/dataFieldsSlice';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { DataFieldsEnum, DataFieldsOptionsEnum } from '../../../shared/types/DataField';
import { GetDataFieldOptionTemplate } from '../../utils/dataFieldOptionTemplate';
import NavButton from '../NavButton';

import './styles.css';
import UserDisplay from '../UserDisplay/UserDisplay';
import ModalWidget from '../ModalWidget/ModalWidget';
import { useLocation } from 'react-router';
const Logo = require('../../assets/img/constellation-logo.png');

const pages = [
  {
    text: 'My SEPs',
    link: '/',
  },
  {
    text: 'All SEPs',
    link: '/all-seps',
  },
  {
    text: 'Create an SEP',
    link: '/create',
  },
];
const settings = ['Logout'];

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.solidBlue.main,
  '&:hover': {
    color: theme.palette.gray.main,
  },
}));

const ResponsiveAppBar = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { data } = useGetSettingsQuery();
  const reviewerTools = data
    ?.filter((help) => help.section === 'ReviewerTools')
    .sort((a, b) => a.order - b.order);
  const userHelp = data
    ?.filter((help) => help.section === 'UserHelp')
    .sort((a, b) => a.order - b.order);
  const progressBarHelp = data
    ?.filter((help) => help.name === 'SEP Progress Graphic')
    .sort((a, b) => a.order - b.order);
  const RAIProgressBarHelp = data
    ?.filter((help) => help.name === 'RAI Progress Graphic')
    .sort((a, b) => a.order - b.order);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const theme = useTheme();
  const location = useLocation();
  const isSepDetailPage = /^\/sep\/\d+$/.test(location.pathname);
  const sepID = isSepDetailPage ? location.pathname.split('/')[2] : '';
  const { data: sepData } = useGetSepQuery(isSepDetailPage ? Number(sepID) : skipToken);
  const { data: dataFields } = useGetDataFieldsBySEPIDQuery(isSepDetailPage ? Number(sepID) : skipToken);
  const isSepDesignReview = sepData?.workFlowType === 'SEP Design Review' || sepData?.workFlowType === null;

  const isKeyTopicAISelected = !!(dataFields && dataFields?.find((data: any) => data?.name === DataFieldsEnum.KEY_TOPICS)?.dataFieldOptions.find((val: any) => val?.value === DataFieldsOptionsEnum.ARTIFICIAL_INTELLIGENCE)?.selected);

  const account = useAccount();
  const roleList: any = useMemo(() => {
    if (env.REACT_APP_LOCAL_DEV_ADMIN === 'true') {
      return ['AuthSuperUser'];
    } else {
      return account?.idTokenClaims?.roles || [];
    }
  }, [account]);

  const editPermission =
    roleList && roleList.length
      ? roleList?.includes('AuthSuperUser') ||
        roleList?.includes('AuthSolutionArchitect') ||
        roleList?.includes('AuthPortfolioOwner') ||
        roleList?.includes('AuthSupply') ||
        roleList?.includes('AuthNuclearCyberSecurity') ||
        roleList?.includes('AuthThirdPartySecurity') ||
        roleList?.includes('AuthSecurity') ||
        roleList?.includes('AuthEA') ||
        roleList?.includes('AuthLegal')
        ? true
        : false
      : false;

  let isSuperUser;
  if (env.REACT_APP_LOCAL_DEV_ADMIN === 'true') {
    isSuperUser = true;
  } else {
    isSuperUser =
      roleList?.includes('AuthSuperUser') &&
      (process.env.NODE_ENV === 'test'
        ? process.env.REACT_APP_SHOW_ADMIN_FEATURES === 'true'
        : env.REACT_APP_SHOW_ADMIN_FEATURES === 'true');
  }

  const navigate: NavigateFunction = useNavigate();
  const { data: loggedInUser } = useGetUserQuery('me');
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const userName: string = loggedInUser?.displayName ?? '';

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleChangeRoute = (route: string) => {
    navigate(route);
    handleCloseNavMenu();
  };

  const isRouteActive = (route: string): boolean => {
    return location.pathname === route;
  };

  return (
    <AppBar
      elevation={0}
      position='sticky'
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.common.white,
        borderBottom: '1px',
        borderColor: theme.palette.gray.lightGray1,
        borderStyle: 'solid',
        zIndex: location.pathname.includes('/task/') ? '999' : ''
      }}
      className="no-print"
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          <a href="/">
            <img
              src={Logo}
              height="50px"
              alt="Constellation"
              style={{ marginTop: '5px', display: 'block' }}
            />
          </a>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ flexGrow: 0, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{
                color: theme.palette.gray.solidGray,
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.text}
                  onClick={() => handleChangeRoute(page.link)}
                  sx={{
                    backgroundColor: isRouteActive(page.link) ? theme.palette.primary.lightHover || 'rgba(35, 114, 185, 0.05)' : 'transparent',
                    color: isRouteActive(page.link) ? theme.palette.primary.main : theme.palette.gray.solidGray,
                    fontWeight: isRouteActive(page.link) ? 600 : 400,
                  }}
                >
                  <Typography textAlign="center" sx={{ textTransform: 'uppercase' }}>{page.text}</Typography>
                </MenuItem>
              ))}
              {isSuperUser && (
                <MenuItem
                  key="superUser"
                  onClick={() => handleChangeRoute('/admin')}
                  sx={{
                    backgroundColor: isRouteActive('/admin') ? theme.palette.primary.lightHover || 'rgba(35, 114, 185, 0.05)' : 'transparent',
                    color: isRouteActive('/admin') ? theme.palette.primary.main : theme.palette.gray.solidGray,
                    fontWeight: isRouteActive('/admin') ? 600 : 400,
                  }}
                >
                  <Typography sx={{ textTransform: 'uppercase' }}>Admin</Typography>
                </MenuItem>
              )}
            </Menu>
          </Box>
          <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => (
              <NavButton
                key={page.text}
                arial-label="navigation buttons"
                onClick={() => handleChangeRoute(page.link)}
                className="nav-buttons"
                font="normal normal 600 14px/19px Open Sans;"
                sx={{
                  color: isRouteActive(page.link) ? theme.palette.primary.main : theme.palette.gray.solidGray,
                  borderBottom: isRouteActive(page.link) ? `3px solid ${theme.palette.primary.main}` : 'none',
                  paddingBottom: isRouteActive(page.link) ? '8px' : '11px',
                  textTransform: 'uppercase',
                }}
              >
                {page.text}
              </NavButton>
            ))}
            <NavButton
                key="newUser_NavButton"
                arial-label="navigation buttons"
                className="nav-buttons"
                font="normal normal 600 14px/19px Open Sans;"
              >
                <a style={{textDecoration: "none", color: theme.palette.gray.solidGray, textTransform: 'uppercase', }} href={"https://cgazc.sharepoint.com/sites/ConstellationArchitecture/Architecture%20Engagement%20&%20Governance"} target="_blank" rel="noopener">New User Portal</a>
              </NavButton>
            {isSuperUser && (
              <NavButton
                key="superUser_NavButton"
                arial-label="navigation buttons"
                className="nav-buttons"
                font="normal normal 600 14px/19px Open Sans;"
                onClick={() => handleChangeRoute('/admin')}
                sx={{
                  color: isRouteActive('/admin') ? theme.palette.primary.main : theme.palette.gray.solidGray,
                  borderBottom: isRouteActive('/admin') ? `3px solid ${theme.palette.primary.main}` : 'none',
                  paddingBottom: isRouteActive('/admin') ? '8px' : '11px',
                  textTransform: 'uppercase',
                }}
              >
                Admin
              </NavButton>
            )}
          </Box>
          <Box sx={{ flexGrow: 0, mx: 2 }}>
            <IconButton
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
              sx={{
                color: open ? theme.palette.primary.main : theme.palette.gray.solidGray,
              }}
            >
              <HelpIcon sx={{ fontSize: typographyTokens.fontSizes.displaySmall }} />
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              {userHelp?.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={() => window.open(item.value, '_blank')}
                >
                  <Typography sx={{ textTransform: 'uppercase' }}>{item.name}</Typography>
                </MenuItem>
              ))}
              {isSepDetailPage && isSepDesignReview && progressBarHelp && progressBarHelp.length > 0 && (
                <>
                  <Divider />
                  {progressBarHelp.map((item, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => window.open(item.value.replace('{sepID}', sepID), '_blank')}
                    >
                      <Typography sx={{ textTransform: 'uppercase' }}>{item.name}</Typography>
                    </MenuItem>
                  ))}
                </>
              )}
              {isSepDetailPage && isKeyTopicAISelected && RAIProgressBarHelp && RAIProgressBarHelp.length > 0 && (
                <>
               
                  {RAIProgressBarHelp.map((item, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => window.open(item.value.replace('{sepID}', sepID), '_blank')}
                    >
                      <Typography sx={{ textTransform: 'uppercase' }}>{item.name}</Typography>
                    </MenuItem>
                  ))}
                </>
              )}
              {editPermission && (
                <>
                  <Divider />
                  {reviewerTools?.map((item, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => window.open(item.value, '_blank')}
                    >
                      <Typography sx={{ textTransform: 'uppercase' }}>{item.name}</Typography>
                    </MenuItem>
                  ))}
                </>
              )}

            </Menu>
          </Box>
          <Box sx={{ flexGrow: 0 }}>
            {loggedInUser?.id && (
              <Tooltip title={userName}>
                <IconButton sx={{ p: 0 }}>
                <StyledLink to={`/user-profile-settings`}>
                  <UserDisplay showPicture userID={loggedInUser?.id} />
                  </StyledLink>
                </IconButton>
              </Tooltip>
            )}
            <Menu
              sx={{ mt: '35px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem key={setting} onClick={handleCloseUserMenu}>
                  <Typography textAlign="center">{setting}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default ResponsiveAppBar;
